import React from 'react';
import PlayingCard from './PlayingCard';
import { SEAT_LABELS } from '../utils/tableSeats';

const PASS_HINTS = {
  left: 'Pass 3 cards to the player on your left',
  right: 'Pass 3 cards to the player on your right',
  across: 'Pass 3 cards across the table',
  hold: 'Hold this hand — no cards are passed',
};

const DIR_SHORT = {
  left: 'left',
  right: 'right',
  across: 'across',
};

/**
 * Label who the cards came from.
 * Humans: username. Bots: "Bot (left|right|across)" using pass direction.
 */
function receivedFromLabel(fromSeat, seats, passDirection) {
  if (!fromSeat) return 'another player';
  const occ = seats?.[fromSeat];
  const isBot = occ?.type === 'site_bot' || occ?.type === 'external_bot';
  if (isBot) {
    const dir = DIR_SHORT[passDirection] || passDirection || SEAT_LABELS[fromSeat] || fromSeat;
    return `Bot (${dir})`;
  }
  return occ?.username || occ?.botLabel || SEAT_LABELS[fromSeat] || fromSeat;
}

/**
 * Hearts pass UI:
 * 1) Select & confirm 3 cards to pass
 * 2) After receive — hold the 3 new cards up until OK or first play
 */
function HeartsPassPanel({
  floating = false,
  phase,
  passDirection,
  passLabel,
  viewerSeat,
  passStatus,
  selectedCount = 0,
  onConfirmPass,
  receivedCards = null,
  passRevealPending = false,
  onAckReceivedPass,
  /** Who passed to viewer (seat id) */
  receivedFromSeat = null,
  seats = null,
}) {
  // ----- Received-pass hold -----
  if (passRevealPending && viewerSeat && receivedCards?.length) {
    const n = receivedCards.length;
    const fromName = receivedFromLabel(receivedFromSeat, seats, passDirection);
    const panelClass = [
      'spades-bid-panel',
      'hearts-pass-panel',
      'hearts-pass-panel--received',
      floating ? 'spades-bid-panel--float' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={panelClass} role="region" aria-label="Cards received">
        <p className="spades-bid-prompt hearts-received-title">
          {`You received ${n} from ${fromName}`}
        </p>
        <div className="hearts-received-cards hearts-received-cards--compact" aria-hidden={false}>
          {receivedCards.map((c) => (
            <PlayingCard
              key={c.id}
              suit={c.suit}
              rank={c.rank}
              width={40}
              height={56}
              className="hearts-received-card"
            />
          ))}
        </div>
        <div className="spades-host-actions">
          <button
            type="button"
            className="spades-bid-btn spades-bid-btn--primary hearts-received-ok"
            onClick={() => onAckReceivedPass?.()}
          >
            OK
          </button>
        </div>
      </div>
    );
  }

  // ----- Select cards to pass -----
  if (phase !== 'passing' || !viewerSeat) return null;
  if (passDirection === 'hold') return null;

  const mine = passStatus?.[viewerSeat];
  const confirmed = !!mine?.confirmed;
  const count = selectedCount ?? mine?.selectedCount ?? 0;
  const canConfirm = !confirmed && count === 3;

  const others = Object.entries(passStatus || {})
    .filter(([seat]) => seat !== viewerSeat)
    .map(([seat, st]) => ({ seat, done: !!st?.confirmed }));

  const panelClass = [
    'spades-bid-panel',
    'hearts-pass-panel',
    floating ? 'spades-bid-panel--float' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={panelClass} role="region" aria-label="Pass cards">
      <p className="spades-bid-prompt">
        {passLabel || PASS_HINTS[passDirection] || 'Pass cards'}
      </p>
      <p className="hearts-pass-hint">
        {PASS_HINTS[passDirection] || 'Select three cards from your hand, then confirm.'}
      </p>
      <p className="hearts-pass-count" aria-live="polite">
        {confirmed
          ? 'Cards passed — waiting for others…'
          : `${count} of 3 selected — click cards in your hand to toggle`}
      </p>
      {!confirmed && (
        <div className="spades-host-actions">
          <button
            type="button"
            className="spades-bid-btn spades-bid-btn--primary spades-host-btn--wide"
            disabled={!canConfirm}
            onClick={() => onConfirmPass?.()}
            title={!canConfirm ? 'Select exactly 3 cards first' : 'Confirm pass'}
          >
            Confirm pass
          </button>
        </div>
      )}
      {others.length > 0 && (
        <ul className="hearts-pass-status-list">
          {others.map(({ seat, done }) => (
            <li key={seat} className={done ? 'hearts-pass-status--done' : ''}>
              {seat}
              {': '}
              {done ? 'passed' : 'selecting…'}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default HeartsPassPanel;
